import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.scss';

export interface SearchableSelectOption {
  id: string;
  name: string;
}

interface SearchableSelectProps {
  value: string;
  options: SearchableSelectOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

/**
 * A single-select combobox with type-to-filter. Closed, it shows the selected
 * option's name; focused, it opens a filtered list. Click-outside and Escape
 * close it.
 *
 * The list is rendered in a portal with fixed positioning so it can never be
 * clipped by a scroll container or hidden behind a modal footer — the reason a
 * plain absolutely-positioned dropdown failed inside the replicate dialog.
 */
export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = 'Search…',
  disabled = false,
  ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selected = useMemo(() => options.find((o) => o.id === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  // Track the input's position so the portalled list stays anchored to it while
  // anything scrolls or the window resizes.
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="searchable-select" ref={wrapRef}>
      <input
        className="searchable-select__input"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
        value={open ? query : (selected?.name ?? '')}
        placeholder={selected?.name || placeholder}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
            (e.target as HTMLInputElement).blur();
          }
        }}
      />

      {open &&
        pos &&
        createPortal(
          <ul
            ref={listRef}
            className="searchable-select__list"
            role="listbox"
            id={listId}
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            {filtered.length === 0 ? (
              <li className="searchable-select__empty">No matches</li>
            ) : (
              filtered.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className={`searchable-select__option${o.id === value ? ' is-selected' : ''}`}
                    // mousedown fires before the input blur, so the choice lands
                    // before the list would otherwise close.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(o.id);
                    }}
                  >
                    {o.name}
                  </button>
                </li>
              ))
            )}
          </ul>,
          document.body
        )}
    </div>
  );
}

export default SearchableSelect;
